@file:Suppress("DEPRECATION")

package org.example.newportfolio.components.sections.nav_header

import androidx.compose.runtime.*
import org.example.newportfolio.components.widgets.button.IconButton
import org.w3c.dom.events.Event
import org.example.newportfolio.models.Section
import org.example.newportfolio.theme.icons.HamburgerIcon
import org.example.newportfolio.theme.icons.IconStyle
import org.example.newportfolio.theme.icons.SmallIconSize
import org.example.newportfolio.utils.UpdateEffect
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.functions.blur
import com.varabyte.kobweb.compose.css.functions.saturate
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.layout.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.components.layout.breakpoint.displayUntil
import com.varabyte.kobweb.silk.components.style.ComponentStyle
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.components.style.toAttrs
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.overlay
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.newportfolio.components.sections.nav_header.components.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Header
import org.w3c.dom.asList

val NavHeaderStyle by ComponentStyle(extraModifiers = { SmoothColorStyle.toModifier() }) {
    val colorPalette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorPalette.background.toRgb().copyf(alpha = 0.8f))
            .position(Position.Sticky)
            .top(0.percent)
            .backdropFilter(saturate(180.percent), blur(5.px))
            .boxShadow(spreadRadius = 2.px, color = colorPalette.overlay)
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
            // Above the hero content (StartSectionGridStyle z-index 1). They
            // previously shared z-index 1, so the later-in-DOM hero photo
            // painted OVER the sticky header while scrolling — the figure
            // appeared "above the header". 50 keeps it under the chatbot
            // (1000) and the scroll-progress bar (9999).
            .zIndex(50)
    }
    Breakpoint.ZERO {
        Modifier
            .padding(left = 12.px, right = 12.px, top = 10.px, bottom = 10.px)
            .height(64.px) // taller mobile header
    }
    Breakpoint.SM {
        Modifier
            .padding(left = 16.px, right = 16.px, top = 12.px, bottom = 12.px)
            .height(68.px) // slightly taller on small tablets
    }
    Breakpoint.MD {
        Modifier
            .padding(left = 24.px, right = 24.px, top = 10.px, bottom = 10.px) // tightened
            .height(6.vh)
    }
    Breakpoint.LG {
        Modifier
            .padding(left = 40.px, right = 40.px, top = 16.px, bottom = 16.px) // reduced horizontal & vertical padding
            .height(9.vh) // slightly smaller height
    }
    Breakpoint.XL {
        Modifier
            .padding(left = 80.px, right = 80.px, top = 16.px, bottom = 16.px) // reduced spacing
            .height(9.vh)
    }
}

// NOTE: To shrink the green active indicator dot & reduce spacing between nav labels,
// please provide the NavBar composable (where the indicator is rendered).

@Composable
fun NavHeader() {
    var colorMode by ColorMode.currentState

    val sections = remember { document.getElementsByClassName("section-container").asList() }
    var selectedSectionId by remember { mutableStateOf(Section.Start.id) }

    // Use UpdateEffect to avoid unnecessary scrolling to the StartSection when the site is first visited
    UpdateEffect(selectedSectionId) {
        window.history.replaceState(null, "", "#$selectedSectionId")
    }

    DisposableEffect(Unit) {
        var ticking = false
        val handler: (Event) -> Unit = { _ ->
            if (!ticking) {
                ticking = true
                window.requestAnimationFrame {
                    val top = window.scrollY
                    sections.forEach { section ->
                        val positionInfo = section.getBoundingClientRect()
                        val offset = positionInfo.top + top - 250
                        val height = positionInfo.height
                        if (top >= offset && top < offset + height) {
                            selectedSectionId = section.id
                        }
                    }
                    ticking = false
                }
            }
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    Header(
        attrs = NavHeaderStyle.toAttrs()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .displayUntil(Breakpoint.LG),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            var sideMenuState by remember { mutableStateOf(SideMenuState.CLOSED) }
            if (sideMenuState != SideMenuState.CLOSED) {
                SideMenu(
                    menuState = sideMenuState,
                    selectedSectionId = selectedSectionId,
                    onCloseRequest = { sideMenuState = sideMenuState.close() },
                    onAnimationEnd = {
                        if (sideMenuState == SideMenuState.CLOSING) {
                            sideMenuState = SideMenuState.CLOSED
                        }
                    }
                )
            }
            Box(modifier = Modifier.flex(1), contentAlignment = Alignment.CenterStart) {
                IconButton(
                    // Explicit equal width/height → borderRadius(50%) renders a
                    // TRUE circle. With auto/auto a non-square glyph produced an
                    // ellipse. 44px matches the theme toggle for a balanced pair
                    // and a comfortable ≥44px touch target. Icon trimmed to
                    // 1.55rem so both header glyphs carry identical visual weight.
                    modifier = Modifier.size(44.px),
                    onClick = { sideMenuState = SideMenuState.OPEN }
                ) {
                    HamburgerIcon(modifier = IconStyle.toModifier().size(1.55.cssRem))
                }
            }
            Box(modifier = Modifier.flex(1), contentAlignment = Alignment.Center) {
                AppLogo(colorMode = colorMode)
            }
            Box(modifier = Modifier.flex(1), contentAlignment = Alignment.CenterEnd) {
                ThemeIconButton(
                    colorMode = colorMode,
                    onClick = { colorMode = colorMode.opposite }
                )
            }
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .displayIfAtLeast(Breakpoint.LG),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(modifier = Modifier.flex(1), contentAlignment = Alignment.CenterStart) {
                AppLogo(colorMode = colorMode)
            }
            NavBar(selectedSectionId = selectedSectionId)
            Box(modifier = Modifier.flex(1), contentAlignment = Alignment.CenterEnd) {
                ThemeIconButton(
                    colorMode = colorMode,
                    onClick = { colorMode = colorMode.opposite }
                )
            }
        }
    }
}